import {
  CollectionSummary,
  InventoryItemSort,
  createInventoryItemComparator,
} from '@qkindex/contracts';
import { scopeRecord } from './multiCollectionIdentity';
import { useSharedCollectionInventory } from './useSharedCollectionInventory';

export interface MultiCollectionInventoryOptions {
  userId: string;
  collections: CollectionSummary[];
  collectionIds: string[];
  itemSort?: InventoryItemSort;
}

export function useMultiCollectionInventory({
  userId,
  collections,
  collectionIds,
  itemSort,
}: MultiCollectionInventoryOptions) {
  const ids = collectionIds.filter((id) =>
    collections.some((entry) => entry.collectionId === id),
  );
  const inventory = useSharedCollectionInventory(
    userId,
    ids,
    ['items', 'locations'],
    itemSort,
  );
  const failed = inventory.entries.filter(
    (entry) =>
      entry.status === 'error' || entry.items.failed || entry.locations.failed,
  );
  const unavailable = inventory.entries.filter(
    (entry) => entry.status === 'unavailable',
  );
  const valid = inventory.entries.filter(
    (entry) => entry.status !== 'error' && !unavailable.includes(entry),
  );
  const ready = valid.filter(
    (entry) => entry.status === 'ready' && entry.summary,
  );
  let items = ready.flatMap((entry) =>
    entry.items.records.map((item) => scopeRecord(item, entry.summary!)),
  );
  if (itemSort) {
    const compare = createInventoryItemComparator(itemSort);
    items.sort(compare);
    // Merge only a globally safe prefix: an unfinished collection may still
    // contain items that precede the faster collection's remaining records.
    if (
      valid.some(
        (entry) =>
          entry.status !== 'ready' ||
          (!entry.items.initialized && !entry.items.failed),
      )
    )
      items = [];
    else {
      const unfinished = ready.filter(
        (entry) =>
          entry.items.nextCursor &&
          !entry.items.refreshing &&
          !entry.items.failed,
      );
      if (unfinished.some((entry) => !entry.items.orderedThrough)) items = [];
      else if (unfinished.length) {
        const frontier = unfinished
          .map((entry) =>
            scopeRecord(entry.items.orderedThrough!, entry.summary!),
          )
          .sort(compare)[0];
        items = items.filter((item) => compare(item, frontier) <= 0);
      }
    }
  }
  const locations = ready.flatMap((entry) =>
    entry.locations.records.map((location) =>
      scopeRecord(location, entry.summary!),
    ),
  );
  const loading = valid.some(
    (entry) =>
      entry.status === 'loading' ||
      [entry.items, entry.locations].some(
        (resource) => !resource.initialized && !resource.failed,
      ),
  );
  const hasMore = ready.some((entry) =>
    [entry.items, entry.locations].some(
      (resource) => Boolean(resource.nextCursor) && !resource.failed,
    ),
  );
  return {
    itemsRevision: JSON.stringify(
      ready.map((entry) => [entry.id, entry.items.dataRevision]),
    ),
    revision: inventory.revision,
    items,
    locations,
    loading,
    hasMore,
    completeLocationCollectionIds: ready
      .filter(
        (entry) =>
          entry.locations.confirmedOnline &&
          !entry.locations.nextCursor &&
          !entry.locations.busy &&
          !entry.locations.refreshing &&
          !entry.locations.failed &&
          !entry.offline,
      )
      .map((entry) => entry.id),
    offline: inventory.entries.some((entry) => entry.offline),
    refreshing: ready.some(
      (entry) => entry.items.refreshing || entry.locations.refreshing,
    ),
    loadingMore: ready.some((entry) =>
      [entry.items, entry.locations].some(
        (resource) =>
          resource.busy && resource.initialized && !resource.refreshing,
      ),
    ),
    incomplete:
      loading || hasMore || failed.length > 0 || unavailable.length > 0,
    error: failed.length
      ? 'Some collections could not be loaded. Retry to search all selected collections.'
      : null,
    failedCollectionIds: failed.map((entry) => entry.id),
    unavailableCollectionIds: unavailable.map((entry) => entry.id),
    collections: ready.map((entry) => ({
      ...collections.find((collection) => collection.collectionId === entry.id),
      ...entry.summary!,
    })),
    loadMore: inventory.loadMore,
    retry: inventory.retry,
    refresh: inventory.refresh,
  };
}
